import type { CSSProperties, ReactNode } from "react";

/**
 * Kurtynowe przejście między sekcjami (Lusion/Lenis-style) - czysty CSS,
 * zero JS, zero kosztu wydajnościowego.
 *
 * Mechanika: sekcja Base dostaje position: sticky z ujemnym top
 * (min(0px, calc(100vh - 100%)) - trik dla sekcji wyższych niż viewport:
 * przykleja się dopiero, gdy jej DÓŁ dojedzie do dołu ekranu). Sekcja Over
 * ma wyższy z-index i własne kryjące tło, więc nasuwa się na Base jak kurtyna.
 *
 * Użycie (zielona sekcja Zasad nasuwa się na sekcję Sprint):
 *   <CurtainStack>
 *     <CurtainStack.Base>
 *       <section id="sprint">...bez zmian...</section>
 *     </CurtainStack.Base>
 *     <CurtainStack.Over>
 *       <section id="principles">...bez zmian (ma kryjące tło #BEF264)...</section>
 *     </CurtainStack.Over>
 *   </CurtainStack>
 *
 * Wymagania: Over MUSI mieć nieprzezroczyste tło (sekcja Zasad ma).
 * Reduced-motion: to pozycjonowanie scrolla, nie animacja - działa tak samo,
 * bez ruchu ponad to, co robi sam użytkownik scrollując.
 */
export function CurtainStack({ children, className, style }: { children: ReactNode; className?: string; style?: CSSProperties }) {
  return (
    <div className={className} style={{ position: "relative", ...style }}>
      {children}
    </div>
  );
}

function Base({ children }: { children: ReactNode }) {
  return (
    <div
      style={{
        position: "sticky",
        top: "min(0px, calc(100vh - 100%))",
        zIndex: 0,
        // Force GPU compositing layer on iOS Safari so that CSS animations
        // inside this sticky container render correctly (known iOS bug).
        transform: "translateZ(0)",
        WebkitTransform: "translateZ(0)",
      }}
    >
      {children}
    </div>
  );
}

function Over({ children, shadow = true }: { children: ReactNode; shadow?: boolean }) {
  return (
    <div
      style={{
        position: "relative",
        zIndex: 10,
        boxShadow: shadow ? "0 -60px 120px rgba(0, 0, 0, 0.45)" : undefined,
      }}
    >
      {children}
    </div>
  );
}

CurtainStack.Base = Base;
CurtainStack.Over = Over;
